/**
 * LaTeX 公式求值工具
 * 從 NsectionB10Page 提取為共用工具，供 FormulaConfigPage 及各公式頁面使用。
 *
 * 限制：含 \sum / \prod / \int 等聚合符號的公式無法自動求值，會回傳 null。
 */

export interface EvalParam {
  symbol: string;
  trial_value: number | null;
}

// 將頂層單字元下標 _x 正規化為 _{x}，避免符號代入時找不到
// 深度 > 0（在 {} 內）的 _x 不處理，防止改到 F_{CH_4,...} 內的 _4
export function normalizeSubscripts(expr: string): string {
  let result = '';
  let depth = 0;
  let i = 0;
  while (i < expr.length) {
    const ch = expr[i];
    if (ch === '\\') { result += ch + (expr[i + 1] ?? ''); i += 2; }
    else if (ch === '{') { depth++; result += ch; i++; }
    else if (ch === '}') { depth--; result += ch; i++; }
    else if (depth === 0 && ch === '_' && i + 1 < expr.length && /[A-Za-z0-9]/.test(expr[i + 1])) {
      result += '_{' + expr[i + 1] + '}'; i += 2;
    }
    else { result += ch; i++; }
  }
  return result;
}

// 找到 expr[openPos] 的 '{' 對應的 '}' 位置（支援嵌套）
function findClosingBrace(expr: string, openPos: number): number {
  let depth = 0;
  for (let i = openPos; i < expr.length; i++) {
    if (expr[i] === '{') depth++;
    else if (expr[i] === '}') { depth--; if (depth === 0) return i; }
  }
  return expr.length - 1;
}

// 遞迴展開 \frac{...}{...}（支援分子/分母含巢狀括號）
function expandFrac(expr: string): string {
  let result = '';
  let i = 0;
  while (i < expr.length) {
    if (expr.startsWith('\\frac', i)) {
      i += 5;
      if (expr[i] === '{') {
        const numEnd = findClosingBrace(expr, i);
        const num = expandFrac(expr.slice(i + 1, numEnd));
        i = numEnd + 1;
        if (expr[i] === '{') {
          const denEnd = findClosingBrace(expr, i);
          const den = expandFrac(expr.slice(i + 1, denEnd));
          i = denEnd + 1;
          result += `(${num})/(${den})`;
          continue;
        }
      }
    }
    result += expr[i++];
  }
  return result;
}

export function evaluateLatex(latex: string, params: EvalParam[]): number | null {
  try {
    let expr = latex.includes('=')
      ? latex.split(/(?<![<>!])=(?!=)/).slice(1).join('=')
      : latex;

    // 1. 正規化頂層單字元下標（OX_y → OX_{y}），確保符號代入能匹配
    expr = normalizeSubscripts(expr);

    // 2. 符號代入（最長優先，避免短符號誤替換長符號的一部分）
    const filled = [...params]
      .filter(p => p.trial_value !== null && p.symbol.trim())
      .sort((a, b) => b.symbol.length - a.symbol.length);

    for (const p of filled) {
      const sym = normalizeSubscripts(p.symbol);
      const escaped = sym.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      expr = expr.replace(new RegExp(escaped, 'g'), `(${p.trial_value})`);
    }

    // 3. \frac 展開（支援嵌套 {}）
    expr = expandFrac(expr);

    // 4. 其餘 LaTeX 轉換
    expr = expr
      .replace(/\\sqrt\{([^}]*)\}/g, 'Math.sqrt($1)')
      .replace(/\\(?:sum|prod|int)(?:_\{[^}]*\})?(?:\^\{[^}]*\})?/g, '')
      .replace(/\\times/g, '*').replace(/\\div/g, '/').replace(/\\cdot/g, '*')
      .replace(/\\left\s*\(/g, '(').replace(/\\right\s*\)/g, ')')
      .replace(/\\left\s*\[/g, '(').replace(/\\right\s*\]/g, ')')
      .replace(/\\left\s*\\{/g, '(').replace(/\\right\s*\\}/g, ')')
      .replace(/\[/g, '(').replace(/\]/g, ')')
      .replace(/\^\{([^}]*)\}/g, '**($1)').replace(/\^([0-9]+)/g, '**$1')
      .replace(/\\[a-zA-Z]+/g, '')
      .replace(/\{/g, '(').replace(/\}/g, ')').replace(/_/g, '');

    if (/[^0-9+\-*/().%\s,MathsqrtpowPIE]/.test(expr.replace(/Math\.[a-z]+/g, ''))) return null;

    // eslint-disable-next-line no-new-func
    const result = new Function(`return (${expr})`)();
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return Math.round(result * 100000) / 100000;
  } catch {
    return null;
  }
}

/**
 * 從 LaTeX 字串解析參數符號（右側變數）
 * 排除純數字、LaTeX 指令、已知希臘字母命令
 */
const LATEX_COMMANDS = new Set([
  'sum','prod','int','frac','sqrt','times','div','left','right',
  'text','mathrm','mathbf','mathit',
  'alpha','beta','gamma','delta','Delta','sigma','mu','eta','theta',
  'lambda','infty','approx','leq','geq','neq','partial','cdot',
  'ldots','overline','hat','bar','vec','dot','ddot',
]);

export function parseLatexSymbols(latex: string): string[] {
  // 取等號右側
  const rhs = latex.includes('=')
    ? latex.split(/(?<![<>!])=(?!=)/).slice(1).join('=')
    : latex;

  const tokens = new Set<string>();

  // 匹配下標/上標：
  //   base_{...}   → braced subscript
  //   base_x       → single-char subscript (no braces, e.g. OX_y)
  //   base^{...}   → superscript
  const subSup = /([A-Za-z][A-Za-z0-9]*)(?:_(?:\{([^}]*)\}|([A-Za-z0-9]))|\^\{([^}]*)\})?/g;
  let m: RegExpExecArray | null;
  while ((m = subSup.exec(rhs)) !== null) {
    const base = m[1];
    if (!LATEX_COMMANDS.has(base) && !/^\d+$/.test(base)) {
      const subBraced = m[2];   // _{...}
      const subSingle = m[3];   // _x
      if (subBraced !== undefined) {
        tokens.add(`${base}_{${subBraced}}`);
      } else if (subSingle !== undefined) {
        tokens.add(`${base}_{${subSingle}}`);
      } else {
        tokens.add(base);
      }
    }
  }

  return Array.from(tokens);
}
